<template>
  <div class="sci-view-container">
    <!-- Header -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button class="sci-btn-back" @click="home">
          <i class="fas fa-arrow-left"></i>
          <span>{{ $t('gestionale') || 'Gestionale' }}</span>
        </button>
        <div class="sci-title-group">
          <h1 class="sci-title">
            <i class="fas fa-truck-loading me-2 text-primary"></i>
            Carico Magazzino & Riconciliazione Fatture
          </h1>
          <p class="sci-subtitle">Registra le merci in arrivo manualmente o digitalizza le fatture fornitore con l'AI</p>
        </div>
      </div>
      <div class="sci-header-right">
        <router-link to="/magazzino" class="sci-btn sci-btn-secondary">
          <i class="fas fa-boxes me-1"></i>
          <span>Vai al Magazzino</span>
        </router-link>
        <button class="sci-btn sci-btn-secondary" @click="displayProduct" :disabled="isLoading">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': isLoading }"></i>
          <span>Aggiorna</span>
        </button>
      </div>
    </header>

    <!-- Main Spatial Grid -->
    <main class="sci-main-content">
      <!-- Top Bento Section: Invoice Upload Studio -->
      <section class="sci-card upload-studio-card">
        <div class="studio-header">
          <div class="studio-title-group">
            <h2 class="studio-title">
              <i class="fas fa-file-invoice-dollar me-2 text-primary"></i>
              Carica Fattura di Acquisto / DDT Fornitore
            </h2>
            <span class="sci-caption">
              Scatta una foto o carica il PDF: l'intelligenza artificiale riconcilierà i prodotti con il tuo magazzino
            </span>
          </div>
          <!-- Status pill during LLM parsing -->
          <div v-if="parsingInvoiceId" class="parsing-status-pill">
            <div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
            <span>Analisi LLM e parsing fattura in corso...</span>
          </div>
        </div>

        <div class="upload-actions-grid">
          <!-- Hidden inputs -->
          <input 
            type="file" 
            ref="cameraInput" 
            @change="handleFileUpload" 
            accept="image/*" 
            capture="environment" 
            style="display: none" 
          />
          <input 
            type="file" 
            ref="fileInput" 
            @change="handleFileUpload" 
            accept=".pdf,image/*" 
            style="display: none" 
          />

          <!-- Action Cards -->
          <button 
            class="upload-tile-btn tile-camera" 
            @click="$refs.cameraInput.click()" 
            :disabled="uploading || !!parsingInvoiceId"
          >
            <div class="tile-icon-wrap">
              <i class="fas fa-camera"></i>
            </div>
            <div class="tile-text-wrap">
              <strong class="tile-title">Scatta Foto Fattura</strong>
              <span class="tile-sub">Acquisisci direttamente con la fotocamera</span>
            </div>
          </button>

          <button 
            class="upload-tile-btn tile-pdf" 
            @click="$refs.fileInput.click()" 
            :disabled="uploading || !!parsingInvoiceId"
          >
            <div class="tile-icon-wrap">
              <i class="fas fa-file-pdf"></i>
            </div>
            <div class="tile-text-wrap">
              <strong class="tile-title">Carica PDF o File Immagine</strong>
              <span class="tile-sub">Seleziona fatture digitali dalla memoria</span>
            </div>
          </button>
        </div>
      </section>

      <!-- Bottom Bento Section: Ordered Products Waiting for Stocking -->
      <section class="sci-card ordered-table-card">
        <div class="card-table-header">
          <div class="table-title-group">
            <h3 class="table-title">
              <i class="fas fa-clipboard-list me-2 text-primary"></i>
              Merci Ordinate in Attesa di Carico
            </h3>
            <span class="sci-caption">Verifica le quantità arrivate e premi "Carica in Magazzino" per aggiornare le scorte</span>
          </div>
          <span class="sci-badge-count">
            {{ prodotti.length }} articoli in arrivo
          </span>
        </div>

        <div class="table-responsive">
          <table class="sci-table">
            <thead>
              <tr>
                <th>Prodotto & Codice</th>
                <th style="width: 200px;">Quantità Arrivata (gr / pz)</th>
                <th style="width: 180px;">Prezzo Unitario (€/100gr)</th>
                <th style="width: 160px; text-align: right;">Totale (€)</th>
                <th style="width: 200px; text-align: center;">Azione</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(prodotto, i) in prodotti" :key="prodotto.code" class="sci-table-row">
                <!-- Product -->
                <td>
                  <div class="product-cell">
                    <span class="product-title">{{ prodotto.descrizio || prodotto.nome || 'Senza Nome' }}</span>
                    <span class="product-code font-monospace" v-if="prodotto.code">
                      <i class="fas fa-barcode me-1"></i> {{ prodotto.code }}
                    </span>
                  </div>
                </td>

                <!-- Quantity -->
                <td>
                  <input 
                    type="number" 
                    min="0" 
                    v-model.number="prodotto.quantitaordine" 
                    class="sci-table-num-input font-monospace"
                    @change="handleQtyOrderChange(prodotto)"
                  />
                </td>

                <!-- Price (€/100gr) -->
                <td>
                  <div class="sci-currency-field">
                    <span class="currency-symbol">€</span>
                    <input 
                      type="number" 
                      step="0.01" 
                      min="0" 
                      v-model.number="prodotto.prezzo" 
                      class="sci-currency-input font-monospace"
                      @change="handlePriceChange(prodotto)"
                    />
                  </div>
                </td>

                <!-- Row Calculated Total -->
                <td style="text-align: right;">
                  <span class="row-calculated-total font-monospace">
                    {{ ((prodotto.prezzo / 100) * prodotto.quantitaordine).toFixed(2) }} €
                  </span>
                </td>

                <!-- Load Action -->
                <td style="text-align: center;">
                  <button 
                    class="sci-btn sci-btn-primary btn-load-stock" 
                    @click="carica(prodotto.code, prodotto.quantitaordine, prodotto.prezzo, i)"
                  >
                    <i class="fas fa-arrow-down me-1"></i>
                    <span>Carica a Magazzino</span>
                  </button>
                </td>
              </tr>

              <!-- Empty State -->
              <tr v-if="prodotti.length === 0">
                <td colspan="5" class="sci-empty-state">
                  <div class="empty-content">
                    <div class="empty-icon-halo">
                      <i class="fas fa-check-circle text-success"></i>
                    </div>
                    <h3 class="empty-title">Nessun carico merci pendente</h3>
                    <p class="empty-desc">
                      Tutte le merci ordinate risultano già caricate a magazzino. Per avviare un nuovo carico, carica una fattura fornitore in alto oppure imposta gli articoli da ordinare nella scheda Magazzino.
                    </p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </main>

    <!-- Upload Progress Modal Overlay -->
    <div v-if="uploading" class="upload-progress-overlay">
      <div class="upload-progress-box">
        <div class="spinner-border text-primary custom-spinner" role="status"></div>
        <h4 class="mt-3">Caricamento documento in corso...</h4>
        <p class="text-muted">Il file viene inviato in modo sicuro per il parsing ottico AI</p>
      </div>
    </div>

    <!-- RECONCILIATION MODAL -->
    <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
      <div class="sci-card reconcile-modal-card">
        <div class="modal-header">
          <div class="modal-title-group">
            <h3 class="modal-title">
              <i class="fas fa-tasks me-2 text-primary"></i>
              Riconciliazione Fattura Fornitore
            </h3>
            <span class="sci-caption">
              Data documento: {{ currentFattura ? formatDate(currentFattura.created_at) : '—' }}
            </span>
          </div>
          <button class="btn-close-modal" @click="showModal = false">&times;</button>
        </div>

        <div class="modal-body reconcile-modal-body">
          <!-- Left: File Preview -->
          <div class="invoice-preview-col">
            <div class="preview-col-header">
              <h5>Anteprima Documento</h5>
              <button 
                v-if="currentFattura" 
                @click="openExternal(currentFattura.file_path)" 
                class="sci-btn-sm"
              >
                <i class="fas fa-external-link-alt me-1"></i> Apri Esternamente
              </button>
            </div>

            <div class="preview-frame-wrap" v-if="currentFattura">
              <div v-if="!currentFattura.file_path" class="no-file-state">
                <i class="fas fa-file-excel mb-2"></i>
                <p>Nessun file collegato.</p>
              </div>
              <img 
                :src="getInvoiceUrl(currentFattura.file_path)" 
                alt="Fattura" 
                v-else-if="isImage(currentFattura.file_path)" 
                class="preview-img"
              />
              <iframe 
                :src="getInvoiceUrl(currentFattura.file_path)" 
                width="100%" 
                height="100%" 
                v-else 
                class="preview-iframe"
              ></iframe>
            </div>
          </div>

          <!-- Right: Extracted Products -->
          <div class="extracted-products-col">
            <div class="preview-col-header">
              <h5>Articoli Rilevati dall'AI ({{ extractedProducts.length }})</h5>
              <span class="sci-caption">Associa ciascuna riga della fattura al corrispondente prodotto di magazzino</span>
            </div>

            <div class="extracted-table-wrap">
              <table class="sci-reconcile-table">
                <thead>
                  <tr>
                    <th>Articolo in Fattura</th>
                    <th style="width: 100px;">Quantità</th>
                    <th style="width: 100px;">Prezzo (€)</th>
                    <th>Associa a Magazzino</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(prod, idx) in extractedProducts" :key="idx">
                    <td>
                      <input type="text" v-model="prod.nome" class="sci-input-reconcile" />
                    </td>
                    <td>
                      <input type="number" v-model.number="prod.quantita" class="sci-input-reconcile font-monospace" />
                    </td>
                    <td>
                      <input type="number" step="0.01" v-model.number="prod.prezzo" class="sci-input-reconcile font-monospace" />
                    </td>
                    <td>
                      <select v-model="prod.matchedCode" class="sci-select-match">
                        <option :value="null">-- Non associare --</option>
                        <option v-for="p in tuttiProdottiMappati" :key="p.code" :value="p.code">
                          {{ p.descrizio || p.nome }} ({{ p.code }})
                        </option>
                      </select>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="sci-btn sci-btn-secondary" @click="showModal = false">Annulla</button>
          <button 
            class="sci-btn sci-btn-primary" 
            @click="confirmReconciliation" 
            :disabled="!isReadyToConfirm"
          >
            <i class="fas fa-check-circle me-1"></i>
            Conferma e Carica a Magazzino
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { store } from '../store/store.js'

export default {
  name: 'CaricaProdotti',
  data() {
    return {
      prodotti: [],
      tuttiProdottiMappati: [],
      store,
      selectedFile: null,
      uploading: false,
      isLoading: false,
      parsingInvoiceId: null,
      pollingInterval: null,
      showModal: false,
      extractedProducts: [],
      currentFattura: null
    }
  },
  computed: {
    isReadyToConfirm() {
      return this.extractedProducts.some(p => p.matchedCode);
    }
  },
  methods: {
    getToken() {
      try {
        if (!localStorage.token) return localStorage.getItem('token') || '';
        const parsed = JSON.parse(localStorage.token);
        return typeof parsed === 'string' ? parsed : (localStorage.getItem('token') || '');
      } catch (e) {
        return localStorage.getItem('token') || '';
      }
    },
    home() {
      this.$router.push({ name: "Gestionale" });
    },
    displayProduct() {
      this.isLoading = true;
      const token = this.getToken();
      const authHeader = `Bearer ${token}`;
      this.axios.post(this.store.path + 'displayProduct', {
        headers: {
          authorization: authHeader
        }
      }, {
        headers: {
          authorization: authHeader
        }
      }).then((response) => {
        const dataList = Array.isArray(response.data)
          ? response.data
          : (response.data && response.data.messaggio ? response.data.messaggio : []);

        if (dataList.length > 0) {
          this.prodotti = [];
          this.tuttiProdottiMappati = dataList;
          dataList.forEach(element => {
            element.prezzo = element.prezzo ? Number(element.prezzo) : 0;
            element.quantitamin = element.quantitamin ? Number(element.quantitamin) : 0;
            element.quantitaordine = element.quantitaordine ? Number(element.quantitaordine) : 0;
            if (element.stato === "ordinato") {
              this.prodotti.push(element);
            }
          });
        }
      }).catch(err => {
        console.error('Errore caricamento prodotti:', err);
      }).finally(() => {
        this.isLoading = false;
      });
    },
    carica(code, quantitaordine, prezzo, i) {
      const totalePrezzo = (prezzo / 100) * quantitaordine;
      this.axios.post(this.store.path + 'carica', {
        code: code,
        prezzo: totalePrezzo,
        quantitaordine: quantitaordine,
        tm: moment(new Date()).unix(),
        headers: {
          authorization: `Bearer ${JSON.parse(localStorage.token)}`
        }
      }).then((response) => {
        if (response.data && response.data.messaggio) {
          this.prodotti.splice(i, 1);
        } else {
          console.warn('Non è stato possibile caricare questo prodotto');
        }
      }).catch(err => {
        console.error('Errore durante carico prodotto:', err);
      });
    },
    handleQtyOrderChange(prodotto) {
      this.axios.post(this.store.path + 'updateQuantita', {
        code: prodotto.code,
        quantita: prodotto.quantita,
        quantitamin: null,
        quantitaordine: prodotto.quantitaordine,
        headers: {
          authorization: `Bearer ${JSON.parse(localStorage.token)}`
        }
      }).catch(err => console.error('Errore aggiornamento quantita:', err));
    },
    handlePriceChange(prodotto) {
      this.axios.post(this.store.path + 'updatePrezzo', {
        code: prodotto.code,
        prezzo: prodotto.prezzo,
        headers: {
          authorization: `Bearer ${JSON.parse(localStorage.token)}`
        }
      }).catch(err => console.error('Errore aggiornamento prezzo:', err));
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.selectedFile = file;
      this.uploadInvoice();
    },
    async uploadInvoice() {
      if (!this.selectedFile) return;

      this.uploading = true;
      const formData = new FormData();
      formData.append('file', this.selectedFile);

      try {
        const response = await this.axios.post(this.store.path + 'fattura/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            authorization: `Bearer ${JSON.parse(localStorage.token)}`
          }
        });
        this.parsingInvoiceId = response.data.fatturaId;
        this.startPolling();
      } catch (error) {
        console.error('Upload error:', error);
        alert('Errore durante il caricamento della fattura.');
      } finally {
        this.uploading = false;
        this.selectedFile = null;
      }
    },
    startPolling() {
      if (this.pollingInterval) return;
      this.pollingInterval = setInterval(async () => {
        await this.checkInvoiceStatus();
      }, 3000);
    },
    async checkInvoiceStatus() {
      try {
        const response = await this.axios.get(this.store.path + 'fattura', {
          headers: { authorization: `Bearer ${JSON.parse(localStorage.token)}` }
        });
        const invoice = response.data.find(i => i._id === this.parsingInvoiceId);
        if (invoice && invoice.stato === 'PROCESSED') {
          clearInterval(this.pollingInterval);
          this.pollingInterval = null;
          this.parsingInvoiceId = null;
          this.openReconciliation(invoice);
        } else if (invoice && invoice.stato === 'ERROR') {
          clearInterval(this.pollingInterval);
          this.pollingInterval = null;
          this.parsingInvoiceId = null;
          alert('Errore durante il parsing ottico della fattura.');
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    },
    openReconciliation(invoice) {
      this.currentFattura = invoice;
      this.extractedProducts = (invoice.prodotti || []).map(p => {
        const match = this.tuttiProdottiMappati.find(mp => mp.code === p.code || (mp.descrizio && p.nome && mp.descrizio.toLowerCase() === p.nome.toLowerCase()));
        return {
          ...p,
          matchedCode: match ? match.code : null
        };
      });
      this.showModal = true;
    },
    async confirmReconciliation() {
      const payload = {
        fatturaId: this.currentFattura._id,
        products: this.extractedProducts
          .filter(p => p.matchedCode)
          .map(p => ({
            code: p.matchedCode,
            quantita: p.quantita,
            prezzo: p.prezzo,
            nome: p.nome
          }))
      };

      try {
        await this.axios.post(this.store.path + 'fattura/confirm-load', payload, {
          headers: { authorization: `Bearer ${JSON.parse(localStorage.token)}` }
        });
        alert('Merci e prodotti caricati con successo a magazzino!');
        this.showModal = false;
        this.displayProduct();
      } catch (error) {
        console.error('Confirm error:', error);
        alert('Errore durante la conferma del carico riconciliato.');
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('it-IT');
    },
    getInvoiceUrl(filePath) {
      if (!filePath) return '';
      const filename = filePath.split('/').pop();
      let baseUrl = this.store.path.replace('/login/', '/').replace('/login', '/');
      if (!baseUrl.endsWith('/')) baseUrl += '/';
      return `${baseUrl}uploads/${filename}`;
    },
    isImage(filePath) {
      if (!filePath) return false;
      const ext = filePath.split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'jfif', 'pjpeg', 'pjp'].includes(ext);
    },
    openExternal(filePath) {
      window.open(this.getInvoiceUrl(filePath), '_blank');
    }
  },
  beforeMount() {
    this.displayProduct();
  },
  beforeUnmount() {
    if (this.pollingInterval) clearInterval(this.pollingInterval);
  }
}
</script>

<style scoped>
.sci-view-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--sci-bg, #f8fafc);
  color: var(--sci-text-main, #0f172a);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding-bottom: 50px;
}

/* Header */
.sci-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 28px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.04);
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  color: #334155;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.sci-btn-back:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
  color: #0f172a;
  transform: translateX(-2px);
}

.sci-title-group {
  display: flex;
  flex-direction: column;
}

.sci-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
}

.sci-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.85rem;
  color: #64748b;
}

.sci-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Main Content */
.sci-main-content {
  max-width: 1440px;
  margin: 24px auto;
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Top Upload Studio Card */
.upload-studio-card {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.04);
  padding: 24px;
}

.studio-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
}

.studio-title {
  margin: 0 0 4px 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
}

.parsing-status-pill {
  display: inline-flex;
  align-items: center;
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
}

.upload-actions-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

@media (max-width: 600px) {
  .upload-actions-grid {
    grid-template-columns: 1fr;
  }
}

.upload-tile-btn {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 20px 24px;
  border-radius: 14px;
  background: #f8fafc;
  border: 2px dashed #cbd5e1;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
}

.upload-tile-btn:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #2563eb;
  transform: translateY(-2px);
}

.upload-tile-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.tile-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #2563eb;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
}

.tile-pdf .tile-icon-wrap {
  color: #ef4444;
}

.tile-text-wrap {
  display: flex;
  flex-direction: column;
}

.tile-title {
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
}

.tile-sub {
  font-size: 0.82rem;
  color: #64748b;
}

/* Ordered Products Table Card */
.ordered-table-card {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.card-table-header {
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafbfc;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.table-title {
  margin: 0 0 4px 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
}

.sci-badge-count {
  display: inline-flex;
  padding: 4px 12px;
  border-radius: 16px;
  background: #e0f2fe;
  color: #0369a1;
  font-size: 0.82rem;
  font-weight: 700;
}

.sci-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  text-align: left;
}

.sci-table thead th {
  background: #f8fafc;
  color: #475569;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 14px 20px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.sci-table-row td {
  padding: 14px 20px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  transition: background 0.15s;
}

.sci-table-row:hover td {
  background: #f8fafc;
}

.product-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.product-title {
  font-weight: 600;
  color: #0f172a;
  font-size: 0.95rem;
}

.product-code {
  font-size: 0.8rem;
  color: #64748b;
}

.sci-table-num-input {
  width: 120px;
  min-height: 38px;
  padding: 6px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.92rem;
  font-weight: 600;
  color: #0f172a;
  background: #ffffff;
}

.sci-currency-field {
  display: inline-flex;
  align-items: center;
  position: relative;
  width: 120px;
}

.currency-symbol {
  position: absolute;
  left: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #64748b;
  pointer-events: none;
}

.sci-currency-input {
  width: 100%;
  min-height: 38px;
  padding: 6px 8px 6px 26px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.92rem;
  font-weight: 600;
  color: #0f172a;
  background: #ffffff;
  outline: none;
}

.row-calculated-total {
  font-size: 1.05rem;
  font-weight: 800;
  color: #059669;
}

.btn-load-stock {
  min-height: 38px;
  padding: 0 14px;
  font-size: 0.88rem;
}

/* Upload Progress Overlay */
.upload-progress-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(15, 23, 42, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
}

.upload-progress-box {
  background: #ffffff;
  border-radius: 16px;
  padding: 36px 48px;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0,0,0,0.25);
  max-width: 400px;
}

.custom-spinner {
  width: 3.5rem;
  height: 3.5rem;
}

/* Reconciliation Modal */
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.reconcile-modal-card {
  width: 100%;
  max-width: 1300px;
  max-height: 92vh;
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0,0,0,0.25);
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 20px 28px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fafbfc;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
}

.btn-close-modal {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #94a3b8;
  cursor: pointer;
}

.reconcile-modal-body {
  padding: 24px;
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: 24px;
  overflow-y: auto;
  max-height: calc(92vh - 160px);
}

@media (max-width: 900px) {
  .reconcile-modal-body {
    grid-template-columns: 1fr;
  }
}

.invoice-preview-col, .extracted-products-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.preview-col-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.preview-col-header h5 {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
}

.sci-btn-sm {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #2563eb;
  cursor: pointer;
}

.preview-frame-wrap {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  height: 480px;
  background: #f8fafc;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.preview-iframe {
  border: none;
}

.no-file-state {
  text-align: center;
  color: #94a3b8;
}

.extracted-table-wrap {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  max-height: 480px;
  overflow-y: auto;
}

.sci-reconcile-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.sci-reconcile-table thead th {
  background: #f8fafc;
  color: #475569;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  padding: 10px 14px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 5;
}

.sci-reconcile-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}

.sci-input-reconcile {
  width: 100%;
  min-height: 36px;
  padding: 4px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.88rem;
  background: #ffffff;
}

.sci-select-match {
  width: 100%;
  min-height: 36px;
  padding: 4px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.85rem;
  background: #ffffff;
}

.modal-footer {
  padding: 16px 28px;
  background: #fafbfc;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* Empty State */
.sci-empty-state {
  padding: 60px 24px;
  text-align: center;
}

.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 440px;
  margin: 0 auto;
}

.empty-icon-halo {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: #ecfdf5;
  color: #059669;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin-bottom: 16px;
}

.empty-title {
  margin: 0 0 6px 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
}

.empty-desc {
  margin: 0;
  font-size: 0.9rem;
  color: #64748b;
  line-height: 1.5;
}

/* Buttons */
.sci-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  text-decoration: none;
}

.sci-btn-primary {
  background: #10b981;
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.25);
}

.sci-btn-primary:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
}

.sci-btn-secondary {
  background: #ffffff;
  color: #334155;
  border: 1px solid #cbd5e1;
}

.sci-btn-secondary:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #94a3b8;
}

.sci-btn:disabled {
  background: #cbd5e1;
  color: #94a3b8;
  cursor: not-allowed;
  box-shadow: none;
}
</style>
